import React from 'react';
import styled from 'styled-components';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const PageContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  padding: 0 20px 0px;
`;

const MainSection = styled.main`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeroSection = styled.section`
  padding: 60px 150px;
  margin-bottom: 0px;
  
  ${mediaQueries.large} {
    padding: 50px 100px;
  }
  
  ${mediaQueries.medium} {
    padding: 40px 40px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 20px;
  }
`;

const TitleContainer = styled.div`
  margin-bottom: 40px;
  text-align: right;
  line-height: 1.1;
`;

const PageTitle = styled.h1`
  font-size: 40px;
  font-weight: 800;
  margin-bottom: 0px;
`;

const PageSubtitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 30px;

  ${mediaQueries.medium} {
    font-size: 26px;
  }

  `;

const Description = styled.p`
  font-size: 24px;
  line-height: 1.5;
  max-width: 800px;
  margin-bottom: 40px;

  ${mediaQueries.medium} {
    font-size: 20px;
  }
  
  ${mediaQueries.small} {
  }

`;

const HighlightedText = styled.span`
  color: ${({theme}) => theme.colors.secondary};
  font-weight: 700;
`;

const MainImage = styled.img`
  width: 100%;
  max-width: 900px;
  height: auto;
  border-radius: 16px;
  margin: 20px auto 0px;
  display: block;
`;

const ValueSection = styled.section`
  padding: 60px 60px;
  background-color: white;
  border-radius: 0;
  margin: 0 auto -20px;
  text-align: center;
  
  ${mediaQueries.large} {
    padding: 0px 40px;
  }
  
  ${mediaQueries.medium} {
    padding: 30px;
  }
  
  ${mediaQueries.small} {
    padding: 20px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 40px;
  text-align: center;
  width: 100%;

  ${mediaQueries.medium} {
    text-align: left;
  }

`;

const ValueGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin: 0 auto;
  max-width: 900px;
  
  ${mediaQueries.medium} {
    grid-template-columns: 1fr;
  }
`;

const ValueCard = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const ValueTitle = styled.h3`
  font-size: 22px;
  margin-bottom: 15px;
  font-weight: var(--font-weight-semibold);

  ${mediaQueries.medium} {
    font-size: 18px;
  }

  `;

const ValueDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;

  ${mediaQueries.medium} {
    font-size: 16px;
  }
`;

const WorkTogetherSection = styled.section`
  position: relative;
  max-width: 900px;
  margin: 0 auto 100px;
  padding-bottom: 40px;
  padding-top: 40px;
  
  ${mediaQueries.medium} {
    margin-bottom: 120px;
  }
`;

const WorkTogetherImage = styled.img`
  width: 100%;
  max-width: 900px;
  height: auto;
  border-radius: 16px;
  display: block;
`;

const WorkTogetherContent = styled.div`
  position: absolute;
  bottom: -30px;
  left: 20%;
  background-color: #011D3A;
  color: white;
  padding: 40px;
  width: 60%;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  
  ${mediaQueries.medium} {
    left: 10%;
    width: 80%;
    padding: 30px;
  }
  
  ${mediaQueries.small} {
    left: 5%;
    width: 90%;
    padding: 20px;
  }
`;

const WorkTogetherTitle = styled.h2`
  font-size: 32px;
  margin-bottom: 10px;
  line-height: 1.0;
`;

const WorkTogetherText = styled.p`
  font-size: 18px;
  line-height: 1.6;
`;

const DescriptionWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const CenteredDescription = styled(Description)`
  text-align: center;
  margin: 0 auto 40px;
  max-width: 800px;

  ${mediaQueries.medium} {
    text-align: left;
    margin: -20px auto 40px;
  }  
`;

const TalentsSection = styled.section`
  padding: 60px 20px;
  text-align: center;
  margin-bottom: 60px;
`;

const TalentsTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 40px;
`;

const TalentsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
`;

const JobCard = styled.div`
  border: 1px solid #eaeaea;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: left;
`;

const JobTitle = styled.h3`
  font-size: 18px;
  margin-bottom: 5px;
`;

const JobMeta = styled.div`
  display: flex;
  gap: 15px;
  font-size: 14px;
  color: #666;
`;

const JobLocation = styled.span`
  display: flex;
  align-items: center;
`;

const JobType = styled.span`
  display: flex;
  align-items: center;
`;

const JobButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 8px 15px;
  border-radius: 20px;
  text-decoration: none;
  font-size: 14px;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const ContactSection = styled.section`
  padding: 60px 20px;
  background-color: #f8f8f8;
  text-align: center;
  margin-bottom: 0;
  border-radius: 32px 32px 0 0;
`;

const ContactTitle = styled.h2`
  font-size: 32px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 20px;
`;

const ContactDescription = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 40px auto;
  
  ${mediaQueries.medium} {
    margin: 30px auto;
  }
  
  ${mediaQueries.small} {
    margin: 20px auto;
  }
`;

const TalentsButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 30px;
  border-radius: 32px;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  display: inline-block;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const ContactButton = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 12px 30px;
  border-radius: 32px;
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  display: inline-block;
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
`;

const Agence: React.FC = () => {
  return (
    <PageContainer>
      <SEO 
        title="Notre agence" 
        description="Découvrez l'agence Thinkfab, experte en développement logiciel. Notre mantra est la bienveillance et le respect du groupe pour apprendre, partager et évoluer ensemble."
        canonical={`${window.location.origin}/agence`}
        image="/images/agence/wdd-agence.png"
      />
      <Header backgroundColor="transparent" textColor="#DD3114" />
      
      <MainSection>
        <HeroSection>
          <TitleContainer>
            <PageTitle>
              <HighlightedText>We are people.</HighlightedText>
            </PageTitle>
            <PageSubtitle>Humains et ensemble avant tout.</PageSubtitle>
          </TitleContainer>
          
          <Description>
            Notre mantra est <HighlightedText>la bienveillance</HighlightedText> et le respect du groupe, donc de
            chacun. <strong>Nous sommes convaincus que c'est le seul socle pour
            apprendre, partager et évoluer.</strong>
          </Description>
          
          <MainImage src="/images/agence/wdd-agence.png" alt="Équipe de développement" />
        </HeroSection>
        
        <ValueSection>
          <SectionTitle>
            How <HighlightedText>we (do) team</HighlightedText>
          </SectionTitle>
          
          <DescriptionWrapper>
            <CenteredDescription>
              Nous offrons un environnement propice à l'épanouissement dans le
              travail avec la volonté constante d'apprendre et progresser.
              <strong> C'est bon pour le groupe, pour soi-même et pour les projets de nos
              clients.</strong>
            </CenteredDescription>
          </DescriptionWrapper>
          
          <ValueGrid>
            <ValueCard>
              <ValueTitle>Être comme chez vous !</ValueTitle>
              <ValueDescription>
                Nous pensons que les gens donnent le meilleur d'eux-mêmes lorsqu'ils ont le sentiment d'être chez eux. Sans tabou, dans une relation de confiance, avec les limites du respect collectif. Cela rend nos collaborations avec les clients à la mesure à l'intérieur du groupe, en créant des relations fortes durables et la livraison de produits solides et authentiques.
              </ValueDescription>
            </ValueCard>
            
            <ValueCard>
              <ValueTitle>Gérer son temps et remote first</ValueTitle>
              <ValueDescription>
                Travaillez où chez vous et au bureau. Nous avons un environnement de travail virtuel pour rassembler l'équipe, que les personnes (délibérément), est notre envie. Pour respecter le rythme de chacun, les échanges asynchrones au bureau ou autour d'un verre, vous faites chez vous au bureau, comme à la maison.
              </ValueDescription>
            </ValueCard>
          </ValueGrid>
        </ValueSection>
        
        <ButtonContainer>
          <TalentsButton href="/recrutement">Retrouvez nos offres</TalentsButton>
        </ButtonContainer>
        
        <WorkTogetherSection>
          <WorkTogetherImage src="/images/agence/wdd-worktogether.png" alt="Collaboration équipe" />
          <WorkTogetherContent>
            <WorkTogetherTitle>Work together.</WorkTogetherTitle>
            <WorkTogetherTitle style={{ color: '#DD3114', marginBottom: '20px' }}>Be yourself.</WorkTogetherTitle>
            <WorkTogetherText>
            Nous aimons les personnes avec leur caractère et singularités.<br />
            Nous privilégions le travail collaboratif mais nous portons une attention particluière aux individualités.
            </WorkTogetherText>
          </WorkTogetherContent>
        </WorkTogetherSection>


      </MainSection>
      
      <Footer pageContext="Page Agence" />
    </PageContainer>
  );
};

export default Agence;